/**
 * The open Modals, oldest first (review of 3cb31666, 29 October 2026).
 *
 * Every Modal listens for Escape on the window. With one Modal open over
 * another (Copy invite link's dialog over the Sales Partner edit form), one
 * press closed both, and the edit form's unsaved changes were lost; closing
 * the inner one also unlocked the page's scroll while the outer one stayed
 * open. A layer per open Modal fixes both: Escape closes only the topmost,
 * and the page stays locked until the last one closes.
 *
 * A layer is stacked when its Modal opens, so the order is the order the
 * person opened them in. Pure (no React, no DOM beyond the body's style), so
 * the rules are tested directly (modalStack.test.ts).
 */

export interface ModalLayer {
  /** True while no Modal opened after this one is still open. */
  isTop(): boolean;
  /** This Modal closed: off the stack; the last one off unlocks the page. Safe to call twice. */
  release(): void;
}

/** The part of document.body a layer touches. */
export interface ScrollLock { style: { overflow: string } }

const layers: symbol[] = [];

/** A Modal opened: on top of the stack, and the page behind it locked. */
export function openModalLayer(body: ScrollLock): ModalLayer {
  const me = Symbol('modal');
  layers.push(me);
  body.style.overflow = 'hidden';
  let released = false;
  return {
    isTop: () => layers[layers.length - 1] === me,
    release: () => {
      if (released) return;
      released = true;
      const at = layers.indexOf(me);
      if (at !== -1) layers.splice(at, 1);
      if (layers.length === 0) body.style.overflow = '';
    },
  };
}

/** The Escape listener each Modal registers: only the topmost open Modal closes. */
export function escapeClosesTopmost(layer: ModalLayer, close: () => void): (event: { key: string }) => void {
  return (event) => {
    if (event.key === 'Escape' && layer.isTop()) close();
  };
}

/** How many Modals are open (for tests). */
export function openModalCount(): number {
  return layers.length;
}
